import React, { useMemo } from 'react';
import {
	StaticSelect,
	StaticMultiSelect,
	type StaticSelectConsumerProps,
	type StaticMultiSelectConsumerProps
} from '../StaticSelect';
import { BasicUserWithPermissions } from 'App/resources/Model/User';
import { getActiveUsers } from 'Store/selectors/AppSelectors';
import { useTranslation } from 'Components/Helpers/translate';

export type UserGetter = typeof getActiveUsers;

export const useUserOptions = (userGetter: UserGetter = getActiveUsers) => {
	const users = userGetter();
	return useMemo(() => {
		return users.map(user => ({
			...user,
			title: user.name
		}));
	}, [users]);
};

const useSharedProps = (userGetter: UserGetter = getActiveUsers) => {
	const { t } = useTranslation();
	const options = useUserOptions(userGetter);
	const placeholder = `${t('default.select')} ${t('default.user').toLowerCase()}`;
	return { placeholder, options };
};

export const StaticUserSelect = (
	props: StaticSelectConsumerProps<Pick<BasicUserWithPermissions, 'id'>> & { userGetter?: UserGetter }
) => {
	const { userGetter, ...rest } = props;
	const sharedProps = useSharedProps(userGetter);
	return <StaticSelect {...sharedProps} {...rest} />;
};

export const StaticUserMultiSelect = (
	props: StaticMultiSelectConsumerProps<Pick<BasicUserWithPermissions, 'id'>> & { userGetter?: UserGetter }
) => {
	const { userGetter, ...rest } = props;
	const sharedProps = useSharedProps(userGetter);
	return <StaticMultiSelect {...sharedProps} {...rest} />;
};
